'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { useSession } from '@/contexts/session-context'
import { isPostgraduateChatRoute } from '@/lib/postgraduate-chat/route'

interface AtaliaChatConfigProps {
  token: string
}

interface AtaliaChatApi {
  identify: (jwt: string) => Promise<unknown>
  logout: () => Promise<unknown>
}

declare global {
  interface Window {
    // Antes do boot do widget é um array (fila de comandos, ex.:
    // `window.AtaliaChat.push(['identify', jwt])`); depois do boot vira o
    // objeto real. `?.` nos call sites cobre os dois casos sem checagem manual.
    AtaliaChat?: AtaliaChatApi | unknown[]
  }
}

export function AtaliaChatConfig({ token }: AtaliaChatConfigProps) {
  const pathname = usePathname()
  const isHiddenForPostgraduateChat = isPostgraduateChatRoute(pathname)
  const { isSignedIn, user } = useSession()
  // Guarda o `reference` já identificado nesta aba, para não repetir
  // `identify()` a cada re-render/navegação enquanto for o MESMO usuário.
  const identifiedReferenceRef = useRef<string | null>(null)

  useEffect(() => {
    // HN-3170: nas páginas de pós-graduação o widget padrão da Atalia não
    // deve aparecer — o chat comercial (`PostgraduateChat`) assume o
    // lugar dele. O SDK não expõe uma API de mostrar/esconder, então,
    // caso o widget já tenha carregado antes de uma navegação SPA para
    // estas páginas, escondemos o host (`#atalia-webchat-host`) via CSS.
    const host = document.getElementById('atalia-webchat-host')
    if (host) {
      host.style.display = isHiddenForPostgraduateChat ? 'none' : ''
    }

    if (isHiddenForPostgraduateChat) {
      return
    }

    if (document.querySelector('script[data-atalia-webchat]')) {
      return
    }

    const script = document.createElement('script')
    // Override só para teste local (aponta pro wrangler dev da Atalia) — em
    // produção a var não existe e cai no default de sempre.
    script.src = process.env.NEXT_PUBLIC_ATALIA_WEBCHAT_JS_URL || 'https://chat.atalia.ai/webchat.js'
    script.dataset.token = token
    script.dataset.ataliaWebchat = 'true'
    script.async = true
    document.head.appendChild(script)
  }, [token, isHiddenForPostgraduateChat])

  // Identifica o aluno logado no webchat (histórico de conversa segue o
  // aluno entre dispositivos/telas) e desloga quando ele sai da conta. Roda
  // de novo a cada mudança de sessão — cobre tanto quem já chega logado
  // (SSR) quanto login/logout via SPA, sem esperar um reload de página.
  useEffect(() => {
    if (isHiddenForPostgraduateChat) {
      return
    }

    if (!isSignedIn || !user) {
      if (identifiedReferenceRef.current) {
        window.AtaliaChat = window.AtaliaChat || []
        if (Array.isArray(window.AtaliaChat)) {
          window.AtaliaChat.push(['logout'])
        } else {
          window.AtaliaChat.logout().catch(() => {})
        }
        identifiedReferenceRef.current = null
      }
      return
    }

    if (identifiedReferenceRef.current === user.reference) {
      return
    }

    let cancelled = false

    fetch('/api/atalia/identity')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { token?: string | null } | null) => {
        if (cancelled || !data?.token) {
          return
        }
        const jwt = data.token
        window.AtaliaChat = window.AtaliaChat || []
        if (Array.isArray(window.AtaliaChat)) {
          window.AtaliaChat.push(['identify', jwt])
        } else {
          window.AtaliaChat.identify(jwt).catch(() => {})
        }
        identifiedReferenceRef.current = user.reference
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [isSignedIn, user, isHiddenForPostgraduateChat])

  return null
}
